import { selectIsCampaignAndTemplateEditorsFolderBreadcrumbFlagOn } from 'core/userContext';
import {
  CAMPAIGN_HEADER_TEST_ID,
  CampaignHeaderDetails,
} from 'modules/campaigns/lib/components/CampaignHeaderDetails';
import { EditorFolderName } from 'modules/foldering';
import { useCampaignFolderDetails } from 'modules/campaigns/lib/hooks/useCampaignFolderDetails';
import { FolderPageNamespace } from 'modules/foldering';
import { useQueryPlacements } from 'modules/placements/hooks/useQueryPlacements';
import { useSelector } from 'react-redux';
import { match } from 'ts-pattern';
import { spacing } from 'ui/theme';
import { useProjectLocalStorage } from 'utils/hooks/useProjectLocalStorage';
import {
  useCampaignAlertsContext,
  useCampaignContext,
  useEditorContext,
  useTemplateContext,
} from '../../context';
import { CampaignEditorView } from '../../types';
import { getIsDefaultWhatsAppTemplate, getShouldDisableEditCampaignSettings } from '../utils';
import { HeaderContentContainer, StyledSecondaryPageHeader } from './CampaignEditorHeader.styled';
import { EditableCampaignLabels } from './EditableCampaignLabels';
import { CampaignName } from './EditableCampaignName';
import { HeaderActions } from './HeaderActions';

export const getBreadcrumbMetadata = ({
  campaignEditorView,
  campaignId,
  hasSubFolder = false,
  allowEdit = false,
}: {
  campaignEditorView: CampaignEditorView;
  campaignId: number;
  hasSubFolder: boolean;
  allowEdit: boolean;
}) => {
  return match({ campaignEditorView, allowEdit })
    .with({ allowEdit: true, campaignEditorView: CampaignEditorView.Summary }, () => ({
      content: 'EDIT CAMPAIGN',
      href: `/campaigns/${campaignId}?view=Edit`,
    }))
    .otherwise(() => ({
      content: hasSubFolder ? 'ALL' : 'ALL CAMPAIGNS',
      href: `/campaigns/manage`,
    }));
};

export const CampaignEditorHeader = () => {
  const {
    campaign: {
      name: campaignName,
      id,
      templateId,
      createdAt,
      messageMedium,
      campaignState,
      campaignTypeDefinition,
      experiments,
      placementId,
    },
  } = useCampaignContext();
  const { hasRecentTriggeredAlerts } = useCampaignAlertsContext();
  const {
    templateAndContent: { name, templateType },
    templateLocales,
  } = useTemplateContext();
  const { campaignEditorView, setIsAlertHistorySlideOverOpen } = useEditorContext();
  const { placements } = useQueryPlacements(false);

  const shouldDisableEditCampaignSettings = getShouldDisableEditCampaignSettings(campaignState);

  const campaignPlacement = placements.find((placement) => placement.id === placementId);
  const isCampaignFolderDetailsQueryEnabled = useSelector(
    selectIsCampaignAndTemplateEditorsFolderBreadcrumbFlagOn,
  );

  /**
   * Retrieve ID of the folder the user came from from local storage so we can return to it when navigating back to the campaign index.
   * If the user navigated directly to the campaign editor or from the root folder, the last visited folder ID will be 0.
   */
  const [lastVisitedFolderId] = useProjectLocalStorage(FolderPageNamespace.CAMPAIGN_INDEX);

  const { campaignFolderDetails } = useCampaignFolderDetails({
    variables: { folderId: lastVisitedFolderId },
    skip: !isCampaignFolderDetailsQueryEnabled || !lastVisitedFolderId,
  });

  const lastVisitedFolderName = isCampaignFolderDetailsQueryEnabled
    ? (campaignFolderDetails?.name as string)
    : 'Previous Folder';

  const breadcrumbMetadata = getBreadcrumbMetadata({
    campaignEditorView,
    campaignId: id,
    hasSubFolder: !!lastVisitedFolderId,
    allowEdit: !shouldDisableEditCampaignSettings,
  });

  const shouldShowFolderBreadcrumb =
    !!lastVisitedFolderId &&
    !(campaignEditorView === CampaignEditorView.Summary && !shouldDisableEditCampaignSettings);

  const isDefaultWhatsAppTemplate = getIsDefaultWhatsAppTemplate(messageMedium, templateType);

  return (
    <StyledSecondaryPageHeader
      data-test={CAMPAIGN_HEADER_TEST_ID}
      documentTitle={`${campaignName} | Campaigns`}
      breadcrumb={breadcrumbMetadata}
      pageActions={<HeaderActions isDefaultWhatsAppTemplate={isDefaultWhatsAppTemplate} />}
      widthStyle={spacing.PageWidthStyle.normal}
      folderBreadcrumb={
        shouldShowFolderBreadcrumb
          ? {
              href: `/campaigns/manage?folderId=${lastVisitedFolderId}`,
              content: (
                <EditorFolderName
                  name={lastVisitedFolderName}
                  hasIcon={!!campaignFolderDetails?.name}
                />
              ),
            }
          : undefined
      }
      pageSubSection={
        <CampaignHeaderDetails
          templateLocales={templateLocales}
          campaignDetails={{
            id,
            campaignPlacement,
            campaignState,
            campaignTypeDefinition,
            createdAt,
            experiments,
            messageMedium,
          }}
          templateDetails={{ templateId, name }}
          withoutCreatedAt
          hasRecentTriggeredAlerts={hasRecentTriggeredAlerts}
          onViewAlerts={() => setIsAlertHistorySlideOverOpen(true)}
          isDefaultWhatsAppTemplate={isDefaultWhatsAppTemplate}
        >
          <EditableCampaignLabels />
        </CampaignHeaderDetails>
      }
    >
      <HeaderContentContainer>
        <CampaignName />
      </HeaderContentContainer>
    </StyledSecondaryPageHeader>
  );
};
